import {
  cleanWhatsappNumber,
  isValidWhatsappNumber,
  whatsappErrorMessage,
  whatsappErrorParts,
  whatsappTryAgainText,
} from "@picovico/utils";
import type {
  WhatsAppCheckoutFields,
  WhatsAppMe,
  WhatsAppOtpChannel,
} from "custom-types";

/*
 * WhatsApp messaging on the customer site (claude/design/whatsapp-messaging/
 * 04 §3, with pvapp's as-built choices from 04 §4): the checkout opt-in, the
 * success-page note, the mobile OTP's channel and its throttle. Pure, so the
 * screens stay thin and the rules are tested on their own. The number rule
 * itself is shared (`@picovico/utils` `whatsapp.ts`).
 */

type Json = Record<string, unknown>;

const isJson = (value: unknown): value is Json =>
  !!value && typeof value === "object" && !Array.isArray(value);

/**
 * amphitheater's `{data, message, status_code}` envelope or a bare body: the
 * body. A bare body is recognised by one of its `keys`, so an envelope whose
 * `data` is the body and a bare body read the same.
 */
function unwrap(response: unknown, keys: readonly string[]): Json | null {
  if (!isJson(response)) return null;
  if (keys.some((key) => key in response)) return response;
  return isJson(response.data) ? response.data : null;
}

/* ------------------------------------------------------------ the options */

/** Nothing offered: the flag is off, nobody is signed in, or `/whatsapp/me/` failed. */
export const WHATSAPP_ME_OFF: WhatsAppMe = Object.freeze({
  tickets: false,
  food: false,
  otp: false,
  offers: Object.freeze({ available: false, opted_in: false, number: null }),
}) as WhatsAppMe;

/**
 * `GET` / `PATCH /whatsapp/me/` read leniently (pvapp `WhatsAppMe.fromJson`):
 * anything missing or malformed is off, `food` included (an older server
 * without it offers no food box).
 */
export function readWhatsAppMe(response: unknown): WhatsAppMe {
  const body = unwrap(response, ["tickets", "otp", "offers"]);
  if (!body) return WHATSAPP_ME_OFF;
  const offers = isJson(body.offers) ? body.offers : {};
  return {
    tickets: body.tickets === true,
    food: body.food === true,
    otp: body.otp === true,
    offers: {
      available: offers.available === true,
      opted_in: offers.opted_in === true,
      number:
        typeof offers.number === "string" && offers.number.trim()
          ? offers.number.trim()
          : null,
    },
  };
}

/** The WhatsApp controls a checkout shows. */
export interface WhatsAppCheckoutOptions {
  /** "Send my ticket (order) on WhatsApp" and its number. */
  tickets: boolean;
  /** "Also send me offers on WhatsApp (<number>)", inside the first box. */
  offers: boolean;
  /** Where offers go: the customer's verified mobile (`offers.number`). */
  offersNumber?: string | null;
}

export const WHATSAPP_CHECKOUT_NONE: WhatsAppCheckoutOptions = Object.freeze({
  tickets: false,
  offers: false,
  offersNumber: null,
});

/**
 * The verified mobile offers would go to, or null when offers are not
 * offered: `offers.available` and `offers.number` must both be set (pvapp).
 */
export function whatsappOffersNumber(
  me: WhatsAppMe | null | undefined,
): string | null {
  return me?.offers.available && me.offers.number ? me.offers.number : null;
}

/**
 * 04 §3 with pvapp's as-built choices: the ticket box needs
 * `whatsapp.tickets` and `me.tickets`; the food checkout's box needs
 * `whatsapp.tickets` and `me.food` (its own template, so its own
 * readiness). The offers box also needs `whatsapp.promotions` and both
 * `offers.available` and `offers.number` (offers go only to the verified
 * mobile, which the label names). It shows even when the customer has
 * already opted in. `me` null (not read yet, refused, failed) offers
 * nothing, so a checkout never waits on it.
 */
export function whatsappCheckoutOptions({
  kind = "ticket",
  ticketsFlag,
  promotionsFlag,
  me,
}: {
  kind?: WhatsAppSendKind;
  ticketsFlag: boolean;
  promotionsFlag: boolean;
  me: WhatsAppMe | null | undefined;
}): WhatsAppCheckoutOptions {
  const ready = kind === "food" ? me?.food === true : me?.tickets === true;
  const tickets = Boolean(ticketsFlag && ready);
  const offersNumber = tickets && promotionsFlag ? whatsappOffersNumber(me) : null;
  return { tickets, offers: Boolean(offersNumber), offersNumber };
}

/**
 * The profile's "Offers on WhatsApp" needs `whatsapp.promotions` and both
 * `offers.available` and `offers.number`.
 */
export function showsWhatsappOffersToggle({
  promotionsFlag,
  me,
}: {
  promotionsFlag: boolean;
  me: WhatsAppMe | null | undefined;
}): boolean {
  return Boolean(promotionsFlag && whatsappOffersNumber(me));
}

/* ----------------------------------------------------------- the checkout */

/** What a checkout sends on WhatsApp. */
export type WhatsAppSendKind = "ticket" | "food";

/**
 * The checkbox and the success-page note, per checkout. The food checkout
 * says "order" (pvapp's choice): it also picks a movie ticket, so "ticket"
 * would read as that one.
 */
export const WHATSAPP_SEND_COPY: Readonly<
  Record<WhatsAppSendKind, { send: string; sent: string }>
> = Object.freeze({
  ticket: {
    send: "Send my ticket on WhatsApp",
    sent: "Your ticket is on its way to WhatsApp",
  },
  food: {
    send: "Send my order on WhatsApp",
    sent: "Your order is on its way to WhatsApp",
  },
});

export const WHATSAPP_OFFERS_LABEL = "Also send me offers on WhatsApp";

/** "Also send me offers on WhatsApp (9779812345678)": where they go (pvapp). */
export function whatsappOffersLabel(number: string | null | undefined): string {
  return number ? `${WHATSAPP_OFFERS_LABEL} (${number})` : WHATSAPP_OFFERS_LABEL;
}

/** The profile switch's line (pvapp): "Get our offers and news on WhatsApp at <number>." */
export function whatsappOffersHint(number: string | null | undefined): string {
  return number
    ? `Get our offers and news on WhatsApp at ${number}.`
    : "Get our offers and news on WhatsApp.";
}

/**
 * "Try again in 25 seconds." / "… 2 minutes." / "… 1 hour." (pvapp's
 * `tryAgainIn`): the wait for a throttled request, from `retry_after`.
 */
export function tryAgainIn(seconds: number): string {
  return `${whatsappTryAgainText(seconds)}.`;
}

export const WHATSAPP_NUMBER_LABEL = "WhatsApp number";
/** The toast when the number stops a payment (pvapp's snack). */
export const WHATSAPP_CHECK_NUMBER = "Check your WhatsApp number.";

/** "Your ticket is on its way to WhatsApp (9841000000)". */
export function whatsappSentText(
  kind: WhatsAppSendKind,
  number: string,
): string {
  return `${WHATSAPP_SEND_COPY[kind].sent} (${number})`;
}

/**
 * What is wrong with the number field, or null (pvapp's validator):
 * `^(\+?977)?9\d{9}$` once spaces and dashes are removed.
 */
export function whatsappNumberProblem(
  value: string | null | undefined,
): string | null {
  if (!cleanWhatsappNumber(value)) return "Enter your WhatsApp number";
  return isValidWhatsappNumber(value) ? null : "Enter a valid mobile number.";
}

/** What the customer chose in the checkout's WhatsApp box. */
export interface WhatsAppOptInChoice {
  send: boolean;
  number: string;
  offers: boolean;
}

/**
 * The optional fields of payment-initiate and the food checkout (02 §11.3):
 * `whatsapp_to` (the number without spaces and dashes; amphitheater
 * normalises it) when the box is offered and ticked and the number is valid,
 * and `whatsapp_offers: true` only with it and only when that box is offered
 * and ticked. Never `whatsapp_offers: false` (D19: a box left unticked is not
 * an opt-out). Nothing otherwise, so the body is the one it always was.
 */
export function whatsappCheckoutFields(
  options: WhatsAppCheckoutOptions,
  choice: WhatsAppOptInChoice,
): WhatsAppCheckoutFields {
  if (!options.tickets || !choice.send) return {};
  if (whatsappNumberProblem(choice.number)) return {};
  return {
    whatsapp_to: cleanWhatsappNumber(choice.number),
    ...(options.offers && choice.offers ? { whatsapp_offers: true } : {}),
  };
}

/* ------------------------------------------------- the success-page note */

/*
 * The payment page leaves the site for the gateway, so what the checkout
 * asked for is kept in sessionStorage (this tab only) until the callback page
 * shows it: one entry per kind, with the references it belongs to (the
 * `pidx` payment-initiate and the food checkout answer, the booking number,
 * the food order). No new API (04 §3).
 */

const sentKey = (kind: WhatsAppSendKind) => `whatsapp-sent:${kind}`;

function sessionStore(): Storage | null {
  try {
    return typeof window === "undefined" ? null : window.sessionStorage;
  } catch {
    return null;
  }
}

const cleanRefs = (refs: readonly unknown[]): string[] =>
  refs
    .filter(
      (ref): ref is string | number =>
        typeof ref === "string" || typeof ref === "number",
    )
    .map((ref) => String(ref).trim())
    .filter(Boolean);

/**
 * Keep `number` for the payment known by `refs`; null (the box not ticked or
 * not offered) forgets what an earlier checkout of this kind kept.
 */
export function rememberWhatsAppSent(
  kind: WhatsAppSendKind,
  number: string | null | undefined,
  refs: readonly unknown[],
  storage: Storage | null = sessionStore(),
): void {
  if (!storage) return;
  try {
    const keep = cleanRefs(refs);
    if (!number || !keep.length) {
      storage.removeItem(sentKey(kind));
      return;
    }
    storage.setItem(sentKey(kind), JSON.stringify({ number, refs: keep }));
  } catch {
    // Storage full or blocked: the note is a nicety, the payment goes on.
  }
}

/**
 * The number kept for a payment known by any of `refs`, removed so it shows
 * once; null when nothing was kept for it (a kept entry for another payment
 * stays).
 */
export function takeWhatsAppSent(
  kind: WhatsAppSendKind,
  refs: readonly unknown[],
  storage: Storage | null = sessionStore(),
): string | null {
  if (!storage) return null;
  try {
    const raw = storage.getItem(sentKey(kind));
    if (!raw) return null;
    const kept = JSON.parse(raw) as { number?: unknown; refs?: unknown };
    const keptRefs = Array.isArray(kept.refs) ? cleanRefs(kept.refs) : [];
    const wanted = cleanRefs(refs);
    if (!wanted.some((ref) => keptRefs.includes(ref))) return null;
    storage.removeItem(sentKey(kind));
    return typeof kept.number === "string" && kept.number ? kept.number : null;
  } catch {
    return null;
  }
}

/* -------------------------------------------------------- the mobile OTP */

/**
 * How long after a code sent on WhatsApp "Send by SMS instead" appears, when
 * `fallback_channels` has `sms` (04 §3). The 30 s throttle is per channel
 * (D18), so the switch at 20 s is allowed.
 */
export const SMS_FALLBACK_DELAY_MS = 20_000;

export const OTP_WHATSAPP_DESCRIPTION = "We sent a code on WhatsApp to";
export const OTP_SMS_FALLBACK_LABEL = "Send by SMS instead";
export const OTP_SMS_SENT = "We sent a new code by SMS.";

/** POST `/member/verify/otp/` as the screens need it. */
export interface OtpSentInfo {
  verificationId: string | null;
  /** Where the code went; null from a server before WhatsApp (no `channel`). */
  channel: WhatsAppOtpChannel | null;
  /** `fallback_channels` has `sms`. */
  smsFallback: boolean;
}

const channelOf = (value: unknown): WhatsAppOtpChannel | null => {
  const text = typeof value === "string" ? value.trim().toLowerCase() : "";
  return text === "whatsapp" || text === "sms" ? text : null;
};

/** The OTP answer, enveloped or bare; `channel` and `fallback_channels` are optional (02 §10). */
export function readOtpSent(response: unknown): OtpSentInfo {
  const body = unwrap(response, ["verification_id", "channel"]) ?? {};
  const fallbacks = Array.isArray(body.fallback_channels)
    ? body.fallback_channels.map(channelOf)
    : [];
  return {
    verificationId:
      typeof body.verification_id === "string" && body.verification_id
        ? body.verification_id
        : null,
    channel: channelOf(body.channel),
    smsFallback: fallbacks.includes("sms"),
  };
}

/** Whether "Send by SMS instead" is offered (after `SMS_FALLBACK_DELAY_MS`) for this answer. */
export const offersSmsFallback = (sent: OtpSentInfo | null | undefined) =>
  Boolean(sent && sent.channel === "whatsapp" && sent.smsFallback);

/** Seconds from a body's `retry_after` (a number or a numeric string); null without one. */
export function retryAfterSeconds(value: unknown): number | null {
  const seconds =
    typeof value === "number"
      ? value
      : typeof value === "string" && value.trim()
        ? Number(value)
        : NaN;
  return Number.isFinite(seconds) && seconds > 0 ? seconds : null;
}

/**
 * A `Retry-After` header in seconds: delay-seconds or an HTTP date (RFC
 * 9110 §10.2.3); null when absent or unreadable.
 */
export function parseRetryAfter(
  value: unknown,
  now: number = Date.now(),
): number | null {
  if (typeof value !== "string" && typeof value !== "number") return null;
  const text = String(value).trim();
  if (!text) return null;
  if (/^\d+$/.test(text)) return retryAfterSeconds(Number(text));
  const at = Date.parse(text);
  if (Number.isNaN(at)) return null;
  return retryAfterSeconds(Math.ceil((at - now) / 1000));
}

/**
 * The plain message for a refused OTP request (429 `OTP_THROTTLED`), from
 * `retry_after` in seconds (pvapp's wording).
 */
export function otpThrottledMessage(
  seconds: number | null | undefined,
): string {
  const lead = "Too many codes requested.";
  if (!seconds || seconds <= 0)
    return `${lead} Please wait a little and try again.`;
  return `${lead} ${tryAgainIn(seconds)}`;
}

/**
 * The throttle message when an OTP request was refused with 429 (any 429
 * there is the throttle), else null. Reads what the OTP service rejects with:
 * the body plus `http_status`, `retry_after` taken from the body or, failing
 * that, the `Retry-After` header.
 */
export function otpThrottleMessage(error: unknown): string | null {
  if (!isJson(error)) return null;
  const throttled =
    error.http_status === 429 ||
    error.status_code === 429 ||
    error.code === "OTP_THROTTLED";
  if (!throttled) return null;
  return otpThrottledMessage(retryAfterSeconds(error.retry_after));
}

/**
 * What the profile switch says when `PATCH /whatsapp/me/` is refused: 429
 * `THROTTLED` "Too many changes. Try again in N seconds." (minutes or hours
 * past 59 s); a 400 shows the server's sentence ("Offers can only go to your
 * verified mobile number."); anything else `fallback`.
 */
export function offersSaveErrorMessage(
  error: unknown,
  fallback: string,
): string {
  const parts = whatsappErrorParts(error);
  if (parts?.code.toUpperCase() === "THROTTLED" || (isJson(error) && error.status_code === 429)) {
    const seconds = parts?.retryAfter ?? retryAfterSeconds(isJson(error) ? error.retry_after : null);
    return seconds
      ? `Too many changes. ${tryAgainIn(seconds)}`
      : "Too many changes. Please wait a little and try again.";
  }
  return whatsappErrorMessage(error, fallback);
}
